import { initializeApp, FirebaseApp } from "firebase/app";
import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";
import { axiosApiInstance } from "../helpers/axios.helper";
import { registerOwnerServiceWorker } from "./pwa.service";
import { useGlobalStore } from "../stores/global.store";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

const getMessagingInstance = (): Messaging => {
  if (!firebaseConfig.apiKey) {
    throw new Error(
      "Push notifications aren't configured yet (missing VITE_FIREBASE_* env vars)."
    );
  }

  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!messaging) {
    messaging = getMessaging(app);
  }

  return messaging;
};

export const isPushSupported = (): boolean =>
  "Notification" in window && "serviceWorker" in navigator && !!firebaseConfig.apiKey;

export const isPushPermissionGranted = (): boolean =>
  isPushSupported() && Notification.permission === "granted";

// Registers (or re-registers) this browser's push token against the
// currently signed-in user - assumes permission is already granted. Split out
// from requestNotificationPermissionAndRegister() so a returning owner (who
// answered the permission prompt on a previous login) can still have their
// token re-synced to the backend on every login, not just the very first one
// - the browser remembers the permission grant indefinitely, but the server's
// device_fcm_tokens row is tied to whoever last logged in and PATCHed it (or
// may not exist at all if it was cleared, e.g. a database reset).
const registerPushToken = async (): Promise<boolean> => {
  const swRegistration = await registerOwnerServiceWorker();
  if (!swRegistration) {
    throw new Error("Couldn't register the service worker for push notifications.");
  }

  const token = await getToken(getMessagingInstance(), {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  });

  if (!token) {
    throw new Error("Couldn't obtain a push token.");
  }

  await axiosApiInstance.patch("/v1/me/tokens", { device_token: token, platform: "web" });
  return true;
};

// Called right after login/signup via autoRequestPushPermission() below -
// triggers the browser's native permission prompt, so it must run off a
// recent user gesture (the login/signup submit) rather than on a bare app
// boot.
export const requestNotificationPermissionAndRegister =
  async (): Promise<boolean> => {
    if (!isPushSupported()) {
      throw new Error("Push notifications aren't supported in this browser.");
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return false;
    }

    return registerPushToken();
  };

// Fired right after a successful login/signup redirect into the owner
// dashboard. The browser's native permission prompt only shows once per
// origin ("default" the first time, "granted"/"denied" forever after), but
// the backend's token record needs re-syncing on every login regardless -
// otherwise signing out and back in (same browser, permission already
// granted) never re-associates a token with the now-current user. Silent on
// failure/denial: this is an automatic call, not a user action, so there's no
// UI to report errors into.
export const autoRequestPushPermission = async (): Promise<void> => {
  if (!isPushSupported() || Notification.permission === "denied") return;

  try {
    const granted =
      Notification.permission === "granted"
        ? await registerPushToken()
        : await requestNotificationPermissionAndRegister();

    if (granted) {
      listenForForegroundMessages();
    }
  } catch (error) {
    console.error("Failed to auto-request push notification permission", error);
  }
};

// Foreground messages (app tab open) don't trigger the service worker's
// background handler - surface them via the app's own snackbar instead.
export const listenForForegroundMessages = (): void => {
  if (!isPushSupported()) return;

  onMessage(getMessagingInstance(), (payload) => {
    const globalStore = useGlobalStore();
    const title = payload.notification?.title;
    const body = payload.notification?.body;
    globalStore.triggerNotice([title, body].filter(Boolean).join(": "));

    // The snackbar above is invisible if this tab isn't the one on screen
    // (another tab focused, or the browser minimized) - fire a real OS-level
    // notification in that case too, same as the service worker already does
    // for a fully backgrounded/closed tab (see onBackgroundMessage in
    // sw/service-worker.ts).
    if (document.visibilityState !== "visible" && Notification.permission === "granted") {
      const nativeNotification = new Notification(title || "Booqimate", {
        body,
        icon: "/icons/icon-192.png",
      });
      nativeNotification.onclick = () => {
        window.focus();
        nativeNotification.close();
      };
    }

    // A new/changed reservation invalidates the owner's wait-list - nudge
    // any mounted unconfirmed-visits list to refetch instead of leaving it
    // stale until the next manual page load.
    if (payload.data?.type === "reservation") {
      globalStore.bumpWaitListRefresh();
    }
  });
};
